import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authMe, authLogin, authRegister, authLogout, authGoogleSession } from "@/lib/api";
import { syncFromProfile as syncLanguageFromProfile } from "@/i18n";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = unknown, false = unauth, object = authed
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const u = await authMe();
            setUser(u);
            // Mirror the server-persisted language into i18n once we know
            // who's logged in. Safe to fire-and-forget — fails silently
            // for anon callers.
            syncLanguageFromProfile();
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // If returning from Google OAuth (#session_id=...), AuthCallback handles it
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        refresh();
    }, [refresh]);

    const login = useCallback(async (creds) => {
        const u = await authLogin(creds);
        setUser(u);
        return u;
    }, []);
    const register = useCallback(async (body) => {
        const u = await authRegister(body);
        setUser(u);
        return u;
    }, []);
    const exchangeGoogle = useCallback(async (sid) => {
        const u = await authGoogleSession(sid);
        setUser(u);
        return u;
    }, []);
    const logout = useCallback(async () => {
        try {
            await authLogout();
        } catch (e) {
            // Server-side session invalidation is best-effort. Local state is
            // cleared regardless so the user is effectively signed out client-side.
            console.warn("authLogout failed (clearing local state anyway):", e?.message || e);
        }
        setUser(false);
    }, []);

    // Memoize the context value so consumers don't re-render on every parent render.
    const value = useMemo(
        () => ({ user, loading, refresh, login, register, exchangeGoogle, logout }),
        [user, loading, refresh, login, register, exchangeGoogle, logout],
    );

    return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
